import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { apiAdminStopImpersonate, apiLogout } from "../api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { AppearanceSelect } from "./AppearanceSelect";
import { AppLogo } from "./AppLogo";
import { InstallPrompt } from "./InstallPrompt";
import { useAuth } from "../SessionContext";

const navCls = ({ isActive }: { isActive: boolean }) =>
  "nav-link" + (isActive ? " nav-link-active" : "");

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, impersonator, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobileNav = useMediaQuery("(max-width: 720px)");
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const desktopMenuCloseRef = useRef<HTMLButtonElement>(null);

  if (!user) {
    return null;
  }

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileNav) setMenuOpen(false);
  }, [isMobileNav]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    if (isMobileNav) {
      drawerCloseRef.current?.focus();
    } else {
      desktopMenuCloseRef.current?.focus();
    }
  }, [menuOpen, isMobileNav]);

  useEffect(() => {
    if (menuOpen && isMobileNav) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen, isMobileNav]);

  async function logout() {
    setBusy(true);
    try {
      await apiLogout();
      await refresh();
      navigate("/login", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function stopImpersonating(goAdmin: boolean) {
    setBusy(true);
    try {
      await apiAdminStopImpersonate();
      await refresh();
      navigate(goAdmin ? "/admin" : "/", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  const closeMenu = () => setMenuOpen(false);

  const mainNavItems: { to: string; end?: boolean; label: string }[] = [
    { to: "/", end: true, label: "Overview" },
    { to: "/accounts", label: "Accounts" },
    { to: "/categories", label: "Categories" },
    { to: "/transactions", label: "Transactions" },
    { to: "/transfers", label: "Transfers" },
    { to: "/reports", label: "Reports" },
    { to: "/assistant", label: "Assistant" },
    ...(isAdmin ? [{ to: "/admin", label: "Admin" }] : []),
  ];

  const menuFooter = (
    <div className="chrome-menu-footer">
      <p className="muted small chrome-menu-email mb-0 mono">{user.email}</p>
      <AppearanceSelect id="appearance-chrome-menu" layout="stacked" />
      <button
        type="button"
        className="btn-ghost chrome-menu-signout"
        disabled={busy}
        onClick={() => {
          closeMenu();
          void logout();
        }}
      >
        Sign out
      </button>
    </div>
  );

  const menuNav = (
    <nav className="nav-drawer-nav" aria-label="Main">
      {mainNavItems.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} className={navCls} onClick={closeMenu}>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  const menuPanelId = isMobileNav ? "main-nav-drawer" : "chrome-menu-dropdown";

  return (
    <>
      <InstallPrompt />
      <div className="layout">
        <div className="app-chrome-sticky">
        {impersonator ? (
          <div className="impersonation-bar" role="status">
            <span>
              Admin view: signed in as <strong className="impersonation-bar-you">{impersonator.email}</strong>
              {" · "}
              viewing <strong>{user.email}</strong>&apos;s budget
            </span>
            <span className="impersonation-bar-actions">
              <button
                type="button"
                className="btn-ghost btn-sm"
                disabled={busy}
                onClick={() => void stopImpersonating(true)}
              >
                Admin
              </button>
              <button type="button" className="btn-sm" disabled={busy} onClick={() => void stopImpersonating(false)}>
                My account
              </button>
            </span>
          </div>
        ) : null}

        <header className="topbar">
          <button
            type="button"
            className="brand-menu-trigger"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-controls={menuOpen ? menuPanelId : undefined}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <AppLogo />
            <span className="brand-menu-title">Hammie Budget</span>
            <svg className="brand-menu-chevron" width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path fill="currentColor" d="M7 10l5 5 5-5H7z" />
            </svg>
          </button>
        </header>

        {isMobileNav ? (
          <>
            <div
              className={`nav-drawer-backdrop${menuOpen ? " nav-drawer-backdrop--open" : ""}`}
              aria-hidden={!menuOpen}
              onClick={closeMenu}
            />
            <div
              id="main-nav-drawer"
              className={`nav-drawer-panel${menuOpen ? " nav-drawer-panel--open" : ""}`}
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
            >
              <div className="nav-drawer-header">
                <p className="nav-drawer-title">Menu</p>
                <button ref={drawerCloseRef} type="button" className="btn-ghost btn-sm" onClick={closeMenu}>
                  Close
                </button>
              </div>
              {menuNav}
              {menuFooter}
            </div>
          </>
        ) : menuOpen ? (
          <>
            <div className="chrome-menu-backdrop" aria-hidden onClick={closeMenu} />
            <div
              id="chrome-menu-dropdown"
              className="chrome-menu-dropdown"
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
            >
              <div className="chrome-menu-dropdown-header">
                <p className="nav-drawer-title">Menu</p>
                <button ref={desktopMenuCloseRef} type="button" className="btn-ghost btn-sm" onClick={closeMenu}>
                  Close
                </button>
              </div>
              <div className="chrome-menu-dropdown-body">{menuNav}</div>
              {menuFooter}
            </div>
          </>
        ) : null}
        </div>

        <Outlet />
      </div>
    </>
  );
}
